import { useParams } from "react-router-dom";
import {DiaEditorCreateRedirectFunction, useDiaEditor} from "../../components/DiaEditor";
import {
    CreateSiteMutationMutation,
    GetSiteQueryQueryResult,
    Site, UpdateSiteInput,
    UpdateSiteMutationMutation,
    UpdateSiteMutationMutationResult,
    useCreateSiteMutationMutation,
    useGetAffectedBookingsMutation,
    useGetSiteQueryQuery,
    useUpdateSiteMutationMutation
} from "../../generated/graphql";
import React, {ReactElement, useCallback, useState } from "react";
import {useUserContext} from "../../contexts/user-context";
import {NonIdealState} from "../../components/NonIdealState";
import WaitSpinner from "../../components/wait-spinner";
import * as yup from "yup";
import {SiteForm} from "./Form";
import { MutationResult } from "@apollo/client/react";
import AffectedBookingsDialog from "./affectedBookings";
import { useSnackbar } from "notistack";


//const isoDateReg = /\d{4}-[01]\d-[0-3]\d/;

function cleanGraphqlVariables(variables:UpdateSiteInput) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
    delete (variables?.__cloned);
  return variables;
}
const validationSchema = yup.object().shape({
    name: yup.string().min(1),
    siteStructure: yup.object().required(),
    siteStructureType: yup.object().required(),
    sitePricing: yup.object().required(),
    region: yup.object().required(),
    distributor: yup.object().required(),
    subletter: yup.object().required()
});
const siteRelationships = {
    'borough':'connect',
    'distributor':'connect',
    'panels':'create',
    'panels.pack': 'connect',
    'panels.postInstruction': 'connect',
    'panels.panelAvailabilities':'create',
    'region': 'connect',
    'region.regionType': 'connect',

    'siteBuildAndRepairs':'create',
    'siteBuildAndRepairs.buildContractor':'connect',
    'siteBuildAndRepairs.buildType':'connect',
    'siteFilesOther':'create',

    'siteHealthAndSafeties':'create',
    'siteHealthAndSafeties.healthAndSafetyType':'connect',

    'sitePayments.currency':'connect',

    'siteDigitalBookingPricing':'create',

    'siteImages':'create',

    'sitePlannings':'create',
    'sitePlannings.planningStage':'connect',
    'sitePlannings.planningOutcome':'connect',

    'sitePricing':'connect',
    'siteRentals':'create',
    'siteRentals.paymentFrequency':'connect',
    'siteRentals.paymentMethod':'connect',
    'siteRentals.rentalType':'connect',
    'siteStructure':'connect',
    'siteStructureType':'connect',
    'style':'connect',
    'subletter':'connect',
    'tags':'connect',
    'takeoverSite':'connect',
    'tvArea':'connect'
};

export function SiteEditor(): ReactElement {
    const params = useParams();
    const isNew = (params.id=='new');
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const query = useGetSiteQueryQuery({variables:{id:params.id},skip:isNew});
    const [affectedBookingsQuery] = useGetAffectedBookingsMutation();
    const [affectedBookings, setAffectedBookings] = useState([]);
    const [ignoreAffected, setIgnoreAffected] = useState(false);
        const [saveMutation,saveResult] = useUpdateSiteMutationMutation();
    const [createMutation,createResult] = useCreateSiteMutationMutation();
    const save = useCallback(({values})=>{
        return saveMutation({variables:{input:values}});
    },[saveMutation]);
    const create = useCallback(({values})=>{
        return createMutation({variables:{input:values}});
    },[createMutation]);
    const getValue = useCallback((result:GetSiteQueryQueryResult)=>{
        return result.data?.site;
    },[]);
    const getSaveValue = useCallback((result:UpdateSiteMutationMutationResult)=>{
        return result?.data?.updateSite;
    },[]);
    /*const getCreateValue = useCallback((result:CreateSiteMutationMutationResult)=>{
        return result.data?.createSite;
    },[]);*/
    const getCreateRedirect = useCallback<DiaEditorCreateRedirectFunction<MutationResult<CreateSiteMutationMutation>>>(({data,baseUrl})=>{
        return baseUrl+data.data?.createSite?.id;
    },[]);
    const user = useUserContext()?.user;
    // sales team is the only sales team, otherwise null
    const makeBlankValues = useCallback(():Site=>{
        return {
            id:'new',
            address1:'',
            address2:'',
            description:'',
            site_code:'',
            impacts:0,
            takeoverSite:null,
            region:null,
            name: '',
            panels: [],
            latitude: '',
            longitude: '',
            siteStructure: null,
            siteBuildAndRepairs: [],
            siteRentals: [],
            sitePlannings: [],
            sitePayments:[],
            siteHealthAndSafeties: [],
            siteDigitalBookingPricing: [],
            siteFilesOther: [],
            tags: [],
            tvArea: null,
            borough: null,
            build_date: null,
            is_regional: false,
            notes: '',
            postcode: '',
            siteImages: [],
            sitePricing: null,
            siteStructureType: null,
            style: null,
            subletter: null,
            three_words: '',
            galleryImages:[],
            layoutImages:[],
            poster_portfolio_url:'',
            rate_card_url:''
        };
    },[user]);
    const checkAvailabilities = async (values:Site, initialValues:Site) => {
        console.log('[SiteEditor] CAV',ignoreAffected);
        if (ignoreAffected) {
            setIgnoreAffected(false);
            return true;
        }
        const newAvailabilities = [];
        const oldAvailabilities = {};
        if (values.panels) {
            values.panels.forEach((panel) => {
                if (panel.panelAvailabilities) {
                    panel.panelAvailabilities.forEach((pa) => {
                        newAvailabilities.push({id: panel.id + '-' + pa.id, availability: pa});
                    });
                }
            });
        }
        if (initialValues?.panels) {
            initialValues.panels.forEach((panel) => {
                if (panel.panelAvailabilities) {
                    panel.panelAvailabilities.forEach((pa) => {
                        oldAvailabilities[panel.id + '-' + pa.id] = pa;
                    });
                }
            });
        }
        const toCheck = [];
        newAvailabilities.forEach(({id, availability}) => {
            let parts;
            if (availability.availability == 'Live') {
                return;
            }
            if (oldAvailabilities[id]) {
                const oldAvailability = oldAvailabilities[id];
                console.log('compare', oldAvailability, availability);
                if (oldAvailability.availability != availability.availability
                    || oldAvailability.first_week != availability.first_week
                    || oldAvailability.last_week != availability.last_week) {
                    console.log('different!');
                    parts = id.split('-');
                    toCheck.push({
                        panelId: parts[0],
                        status: availability.availability,
                        first_week: availability.first_week,
                        last_week: availability.last_week
                    });
                }
            } else {
                if (availability.availability != 'Live') {
                    parts = id.split('-');
                    toCheck.push({
                        panelId: parts[0],
                        status: availability.availability,
                        first_week: availability.first_week,
                        last_week: availability.last_week
                    });
                }
            }
        });
        enqueueSnackbar('Checking panel bookings...', {key: 'PANEL_CHECK'});

        //const key = AppToaster.show({intent: 'primary', message: 'Checking panel bookings...'});
        if (toCheck.length) {
            const res = await affectedBookingsQuery({variables: {input: toCheck}});
            closeSnackbar('PANEL_CHECK');
//            enqueueSnackbar('Checking panel bookings...', {key: 'PANEL_CHECK'});
            if (!res || !res?.data?.affectedPanelBookings?.length) {
                return true;
            }
            setAffectedBookings(res?.data?.affectedPanelBookings);
            return false;
        }
        closeSnackbar('PANEL_CHECK');
        return true;
    };
    const data = useDiaEditor<Site,GetSiteQueryQueryResult,CreateSiteMutationMutation,UpdateSiteMutationMutation>({save,saveResult,create,createResult,query:query,
        relationships:siteRelationships,
        baseUrl:'/sites/',
        blankValues:makeBlankValues,
        label:'Site',
        permission:'site',
        cleanGraphqlVariables,
        saveGuard:checkAvailabilities,
        validationSchema:validationSchema,
        getValue:getValue,
        getSaveValue:getSaveValue,
        getCreateRedirect:getCreateRedirect
    });

    if (query.error) {
        return <NonIdealState
            severity="error"
            title="An error has occurred"
            message="GraphQL error encountered"
        />;
    }
    if(data.loading) {
        return <WaitSpinner variant="panel"/>;
    }
    const onAccept = () => {
        console.log('[SiteEditor] CAV SET TRUE',ignoreAffected);
        setIgnoreAffected(true);
        setAffectedBookings([]);
        setTimeout(()=>{
            data.form.handleSubmit();
        });
    };
    const onCancel = () => {
        setAffectedBookings([]);
    };

    return <>{data.confirmation}
                    <AffectedBookingsDialog name={data?.values?.name} isOpen={affectedBookings.length}
                                            affectedBookings={affectedBookings} onAccept={onAccept}
                                            onCancel={onCancel}/>
    <SiteForm diaForm={data.form} /></>;
}